import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, Package } from 'lucide-react';

export const OrderSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [orderDetails, setOrderDetails] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('error');
      return;
    }

    checkPaymentStatus(sessionId);
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId) => {
    const maxAttempts = 5;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/payments/stripe/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        setOrderDetails(response.data);
        setStatus('success');
      } else if (response.data.status === 'expired') {
        setStatus('error');
      } else if (pollCount < maxAttempts) {
        // Continue polling
        setPollCount(prev => prev + 1);
        setTimeout(() => checkPaymentStatus(sessionId), 2000);
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Payment status check error:', error);
      if (pollCount < maxAttempts) {
        setPollCount(prev => prev + 1);
        setTimeout(() => checkPaymentStatus(sessionId), 2000);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-purple-25 px-6 py-12">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-lg p-8">
        {status === 'checking' && (
          <div className="text-center">
            <Loader2 className="h-16 w-16 mx-auto mb-4 text-purple-600 animate-spin" />
            <h2 className="text-2xl font-heading font-light mb-2">Processing Your Payment</h2>
            <p className="text-stone-600">Please wait while we confirm your order...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-600" />
            <h2 className="text-3xl font-heading font-light mb-2 text-green-800">
              Order Confirmed!
            </h2>
            <p className="text-lg text-stone-600 mb-6">
              Thank you for your purchase. Your order has been successfully placed.
            </p>

            {orderDetails && (
              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <div className="grid grid-cols-2 gap-4 text-left">
                  <div>
                    <p className="text-sm text-stone-500 mb-1">Amount Paid</p>
                    <p className="text-xl font-semibold text-purple-900">
                      ${(orderDetails.amount_total / 100).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-stone-500 mb-1">Payment Status</p>
                    <p className="text-xl font-semibold text-green-600 capitalize">
                      {orderDetails.payment_status}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <p className="text-stone-600 mb-6">
              <Package className="inline h-5 w-5 mr-2" />
              A confirmation email has been sent to your email address.
            </p>

            <div className="flex gap-4 justify-center">
              <Button
                onClick={() => navigate('/orders')}
                className="rounded-full bg-purple-600 hover:bg-purple-700"
              >
                View Orders
              </Button>
              <Button
                onClick={() => navigate('/shop')}
                variant="outline"
                className="rounded-full"
              >
                Continue Shopping
              </Button>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <span className="text-3xl">⚠️</span>
            </div>
            <h2 className="text-2xl font-heading font-light mb-2 text-red-800">
              Payment Processing Issue
            </h2>
            <p className="text-stone-600 mb-6">
              We couldn't confirm your payment. Please check your email or contact support.
            </p>
            <Button
              onClick={() => navigate('/checkout')}
              className="rounded-full bg-purple-600 hover:bg-purple-700"
            >
              Back to Checkout
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
